import { useState, useEffect } from 'react';
import { X, User, Mail, Phone, AlertCircle, Loader2, Save, Info } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getEmailApiBaseUrl } from '../../utils/emailApi';

// Prefissi internazionali (ordinati per lunghezza decrescente per il parsing)
const PHONE_PREFIXES = [
    { code: '39', label: '+39 Italia' },
    { code: '33', label: '+33 Francia' },
    { code: '34', label: '+34 Spagna' },
    { code: '44', label: '+44 Regno Unito' },
    { code: '41', label: '+41 Svizzera' },
    { code: '43', label: '+43 Austria' },
    { code: '49', label: '+49 Germania' },
    { code: '32', label: '+32 Belgio' },
    { code: '31', label: '+31 Paesi Bassi' },
    { code: '351', label: '+351 Portogallo' },
    { code: '30', label: '+30 Grecia' },
    { code: '48', label: '+48 Polonia' },
    { code: '46', label: '+46 Svezia' },
    { code: '47', label: '+47 Norvegia' },
    { code: '45', label: '+45 Danimarca' },
    { code: '358', label: '+358 Finlandia' },
    { code: '353', label: '+353 Irlanda' },
    { code: '352', label: '+352 Lussemburgo' },
    { code: '356', label: '+356 Malta' },
    { code: '386', label: '+386 Slovenia' },
    { code: '385', label: '+385 Croazia' },
    { code: '420', label: '+420 Rep. Ceca' },
    { code: '421', label: '+421 Slovacchia' },
    { code: '36', label: '+36 Ungheria' },
    { code: '40', label: '+40 Romania' },
    { code: '359', label: '+359 Bulgaria' },
    { code: '355', label: '+355 Albania' },
    { code: '389', label: '+389 Macedonia' },
    { code: '381', label: '+381 Serbia' },
    { code: '383', label: '+383 Kosovo' },
    { code: '387', label: '+387 Bosnia' },
    { code: '382', label: '+382 Montenegro' },
    { code: '1', label: '+1 USA/Canada' },
    { code: '7', label: '+7 Russia' },
    { code: '90', label: '+90 Turchia' },
    { code: '212', label: '+212 Marocco' },
    { code: '213', label: '+213 Algeria' },
    { code: '216', label: '+216 Tunisia' },
    { code: '20', label: '+20 Egitto' },
    { code: 'other', label: 'Altro' }
];

function parseStoredPhone(phoneStr) {
    if (!phoneStr || !String(phoneStr).trim()) return { prefix: '39', number: '' };
    const digits = String(phoneStr).replace(/\D/g, '');
    if (digits.length === 0) return { prefix: '39', number: '' };
    // Prova prefissi dal più lungo
    const byLength = [...PHONE_PREFIXES].filter(p => p.code !== 'other').sort((a, b) => b.code.length - a.code.length);
    for (const p of byLength) {
        if (digits.startsWith(p.code)) {
            const rest = digits.slice(p.code.length);
            return { prefix: p.code, number: rest };
        }
    }
    return { prefix: 'other', number: digits };
}

function parseCanManageOwnWorkouts(value) {
    if (value === undefined || value === null) return true;
    return Number(value) === 1;
}

const EditClientProfileModal = ({ client, isOpen, onClose, onSuccess }) => {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    const api = getAuthenticatedApi();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phonePrefix: '39',
        phoneNumber: '',
        phoneOtherPrefix: '',
        marketingOptIn: false,
        canManageOwnWorkouts: true,
    });

    const [saving, setSaving] = useState(false);
    const [loadingMarketing, setLoadingMarketing] = useState(false);
    const [error, setError] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const emailApi = getEmailApiBaseUrl();

    // Reset form data when client changes or modal opens
    useEffect(() => {
        if (client && isOpen) {
            const { prefix, number } = parseStoredPhone(client.phone);
            setFormData({
                name: client.name || '',
                email: client.email || '',
                phonePrefix: prefix,
                phoneNumber: number,
                phoneOtherPrefix: '',
                marketingOptIn: false,
                canManageOwnWorkouts: parseCanManageOwnWorkouts(client.can_manage_own_workouts),
            });
            setError(null);
            setFormErrors({});

            let cancelled = false;
            const loadMarketing = async () => {
                setLoadingMarketing(true);
                try {
                    const res = await api.get(
                        `gym_email_tags.php?action=member_tags&user_id=${client.id}`,
                        { baseURL: emailApi }
                    );
                    if (!cancelled) {
                        setFormData((prev) => ({
                            ...prev,
                            marketingOptIn: !!res.data?.marketing_opt_in,
                        }));
                    }
                } catch {
                    if (!cancelled) {
                        setFormData((prev) => ({ ...prev, marketingOptIn: false }));
                    }
                } finally {
                    if (!cancelled) {
                        setLoadingMarketing(false);
                    }
                }
            };
            loadMarketing();
            return () => {
                cancelled = true;
            };
        }
    }, [client, isOpen, api, emailApi]);

    const validateForm = () => {
        const errors = {};

        if (!formData.name.trim()) {
            errors.name = t('gym.editClientProfile.errors.nameRequired');
        }

        if (!formData.email.trim()) {
            errors.email = t('gym.editClientProfile.errors.emailRequired');
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = t('gym.editClientProfile.errors.emailInvalid');
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        try {
            setSaving(true);
            setError(null);
            
            const prefixDigits = formData.phonePrefix === 'other'
                ? (formData.phoneOtherPrefix || '').replace(/\D/g, '')
                : formData.phonePrefix;
            const numberDigits = (formData.phoneNumber || '').replace(/\D/g, '');
            const phone = (prefixDigits + numberDigits).trim() || null;

            const dataToSend = {
                name: formData.name,
                email: formData.email,
                phone: phone || ''
            };

            const response = await api.put(`/users.php?id=${client.id}`, dataToSend);

            try {
                await api.post(
                    'gym_email_tags.php?action=set_marketing_opt_in',
                    { user_id: client.id, marketing_opt_in: formData.marketingOptIn },
                    { baseURL: emailApi }
                );
            } catch (marketingErr) {
                // eslint-disable-next-line no-console
                console.error('Errore salvataggio consenso marketing:', marketingErr);
                toast.warn(t('gym.editClientProfile.messages.marketingOptInSaveError'));
            }

            if (client.role_in_gym === 'member' || client.membership_role === 'member') {
                try {
                    await api.put(`/gym_members.php?user_id=${client.id}`, {
                        can_manage_own_workouts: formData.canManageOwnWorkouts ? 1 : 0,
                    });
                } catch (workoutPermErr) {
                    // eslint-disable-next-line no-console
                    console.error('Errore salvataggio permesso schede:', workoutPermErr);
                    toast.warn(t('gym.editClientProfile.messages.canManageOwnWorkoutsSaveError'));
                }
            }
            
            toast.success(t('gym.editClientProfile.messages.updateSuccess'));
            
            // Chiama onSuccess con i dati aggiornati
            if (onSuccess) {
                onSuccess({
                    ...response.data.user,
                    can_manage_own_workouts: formData.canManageOwnWorkouts ? 1 : 0,
                });
            }
            
            onClose();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Errore durante il salvataggio:', err);
            
            const errorMessage = err.response?.data?.error || 
                                t('gym.editClientProfile.messages.updateError');
            
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));

        // Rimuovi l'errore del campo quando l'utente inizia a modificarlo
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="relative flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35">
                {/* Header */}
                <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-[#dad8e5] px-5 py-4 pr-12 dark:border-white/10 dark:bg-gray-800">
                    <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900 dark:font-open-sans dark:text-white">
                        <User className="h-6 w-6 text-blue-600 dark:text-gray-300" />
                        {t('gym.editClientProfile.title')}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="absolute right-3 top-3 rounded-lg p-2 text-gray-500 transition-colors hover:bg-black/5 disabled:opacity-50 dark:text-white/80 dark:hover:bg-white/10"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto bg-white px-5 py-6 dark:bg-gray-900 sm:px-6">
                {/* Client Info */}
                <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-3 dark:border-white/10 dark:bg-gray-900">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                        <strong>{t('gym.editClientProfile.clientInfo.username')}:</strong> {client?.username}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t('gym.editClientProfile.clientInfo.note')}
                    </p>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 flex items-center">
                        <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                        {error}
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-5 pb-1">
                    {/* Nome */}
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 mb-2 font-medium" htmlFor="name">
                            {t('gym.editClientProfile.fields.name')} *
                        </label>
                        <div className="relative">
                            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                disabled={saving}
                                className={`w-full rounded-lg border py-2 pl-10 pr-4 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-white dark:focus:ring-indigo-500/30 ${
                                    formErrors.name 
                                        ? 'border-red-500 dark:border-red-400' 
                                        : 'border-gray-300 dark:border-gray-700 dark:bg-gray-800'
                                } bg-white`}
                                placeholder={t('gym.editClientProfile.placeholders.name')}
                            />
                        </div>
                        {formErrors.name && (
                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.name}</p>
                        )}
                    </div>

                    {/* Email */}
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 mb-2 font-medium" htmlFor="email">
                            {t('gym.editClientProfile.fields.email')} *
                        </label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                disabled={saving}
                                className={`w-full rounded-lg border py-2 pl-10 pr-4 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-white dark:focus:ring-indigo-500/30 ${
                                    formErrors.email 
                                        ? 'border-red-500 dark:border-red-400' 
                                        : 'border-gray-300 dark:border-gray-700 dark:bg-gray-800'
                                } bg-white`}
                                placeholder={t('gym.editClientProfile.placeholders.email')}
                            />
                        </div>
                        {formErrors.email && (
                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.email}</p>
                        )}
                    </div>

                    {/* Telefono: prefisso + numero (opzionale, per WhatsApp) */}
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 mb-2 font-medium">
                            {t('gym.editClientProfile.fields.phone')}
                        </label>
                        <div className="flex gap-2">
                            <div className="relative flex-shrink-0 w-36">
                                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" />
                                <select
                                    id="phonePrefix"
                                    name="phonePrefix"
                                    value={formData.phonePrefix}
                                    onChange={handleInputChange}
                                    disabled={saving}
                                    className="w-full cursor-pointer appearance-none rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-blue-500 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:ring-indigo-500/30"
                                >
                                    {PHONE_PREFIXES.map((p) => (
                                        <option key={p.code} value={p.code}>
                                            {p.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {formData.phonePrefix === 'other' ? (
                                <input
                                    type="tel"
                                    name="phoneOtherPrefix"
                                    value={formData.phoneOtherPrefix}
                                    onChange={handleInputChange}
                                    disabled={saving}
                                    placeholder="+XX"
                                    className="w-20 flex-shrink-0 rounded-lg border border-gray-300 bg-white px-2 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:ring-indigo-500/30"
                                />
                            ) : null}
                            <input
                                type="tel"
                                id="phoneNumber"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleInputChange}
                                disabled={saving}
                                className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-blue-500 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:ring-indigo-500/30"
                                placeholder={t('gym.editClientProfile.placeholders.phoneNumber')}
                            />
                        </div>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {t('gym.editClientProfile.fields.phoneHint')}
                        </p>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-gray-800/40">
                        <label className="flex cursor-pointer items-start gap-3">
                            <input
                                type="checkbox"
                                name="marketingOptIn"
                                checked={formData.marketingOptIn}
                                onChange={handleInputChange}
                                disabled={saving || loadingMarketing}
                                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 dark:border-white/30 dark:bg-gray-800"
                            />
                            <span className="min-w-0 flex-1">
                                <span className="block text-sm font-medium text-gray-900 dark:text-white">
                                    {t('gym.editClientProfile.fields.marketingOptIn')}
                                </span>
                                <span className="mt-1 block text-xs text-gray-500 dark:text-white/60">
                                    {t('gym.editClientProfile.fields.marketingOptInHint')}
                                </span>
                            </span>
                            {loadingMarketing && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" />}
                        </label>
                    </div>

                    {(client?.role_in_gym === 'member' || client?.membership_role === 'member') && (
                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-gray-800/40">
                            <label className="flex cursor-pointer items-start gap-3">
                                <input
                                    type="checkbox"
                                    name="canManageOwnWorkouts"
                                    checked={formData.canManageOwnWorkouts}
                                    onChange={handleInputChange}
                                    disabled={saving}
                                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 dark:border-white/30 dark:bg-gray-800"
                                />
                                <span className="min-w-0 flex-1">
                                    <span className="block text-sm font-medium text-gray-900 dark:text-white">
                                        {t('gym.editClientProfile.fields.canManageOwnWorkouts')}
                                    </span>
                                    <span className="mt-1 block text-xs text-gray-500 dark:text-white/60">
                                        {t('gym.editClientProfile.fields.canManageOwnWorkoutsHint')}
                                    </span>
                                </span>
                            </label>
                        </div>
                    )}

                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg flex items-start gap-3 text-amber-800 dark:text-amber-100">
                        <Info className="h-5 w-5 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="font-medium text-sm">
                                {t('gym.editClientProfile.resetPasswordInfo.title')}
                            </p>
                            <p className="text-sm mt-1 text-amber-700 dark:text-amber-200">
                                {t('gym.editClientProfile.resetPasswordInfo.description')}
                            </p>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end space-x-3 border-t border-gray-200 pt-4 dark:border-white/10">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={saving}
                            className="rounded-xl bg-gray-200 px-4 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-800 dark:font-open-sans dark:text-white dark:hover:brightness-110"
                        >
                            {t('gym.editClientProfile.actions.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    {t('gym.editClientProfile.actions.saving')}
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4" />
                                    {t('gym.editClientProfile.actions.save')}
                                </>
                            )}
                        </button>
                    </div>
                </form>
                </div>
            </div>
        </div>
    );
};

export default EditClientProfileModal;

